import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import { useBusinessDataQuery } from '@/hooks/business/useBusinessDataQuery'
import {
  CashOperationQueryParams,
  CashOperationQueryResponse,
  useCashOperationQuery,
} from '@/hooks/cashOperations/useCashOperationQuery'
import { storage } from '@/lib/utils'

import {
  CashOperationFilters,
  useCashOperationFilters,
} from '../filters/hooks/useCashOperationFilters'

interface CashOperationTableContextType {
  currentPage: number
  setCurrentPage: (_page: number) => void
  pageSize: number
  setPageSize: (_size: number) => void
  cashOperationData: CashOperationQueryResponse | null
  isLoading: boolean
  isFetching: boolean
  isError: boolean
  refetch: () => void
  goToNextPage: () => void
  goToPreviousPage: () => void
  skeletonRowCount: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  resetToFirstPage: () => void
}

const TableContext = createContext<CashOperationTableContextType | undefined>(
  undefined
)

export const INITIAL_PAGE = 1
const INITIAL_PAGE_SIZE = 20

export const CASH_OPERATION_STORAGE_KEYS = {
  CURRENT_PAGE: 'cash-ops-table-current-page',
  PAGE_SIZE: 'cash-ops-table-page-size',
} as const

// Helper to create a filter key for comparison
const createFilterKey = (filters: CashOperationFilters): string => {
  return JSON.stringify({
    operation: filters.operation,
    category: [...filters.category].sort(),
    dateFrom: filters.dateFrom || '',
    dateTo: filters.dateTo || '',
    madeById: filters.madeById || '',
    amountFrom: filters.amountFrom || '',
    amountTo: filters.amountTo || '',
    codeCurrency: filters.codeCurrency || '',
    areaIds: [...filters.areaIds].sort(),
    economicCycleIds: filters.economicCycleIds || '',
  })
}

export const TableContextProvider = ({ children }: { children: ReactNode }) => {
  const { filters } = useCashOperationFilters()

  const { businessData } = useBusinessDataQuery({})

  const previousFiltersRef = useRef<string>('')
  const isInitialMount = useRef(true)

  // Pagination state
  const [currentPage, setCurrentPageState] = useState(() =>
    storage.get(CASH_OPERATION_STORAGE_KEYS.CURRENT_PAGE, INITIAL_PAGE)
  )
  const [pageSize, setPageSizeState] = useState(() =>
    storage.get(CASH_OPERATION_STORAGE_KEYS.PAGE_SIZE, INITIAL_PAGE_SIZE)
  )

  // Check if filters have changed (excluding pagination-related changes)
  const currentFilterKey = createFilterKey(filters)
  const filtersHaveChanged =
    previousFiltersRef.current !== currentFilterKey && !isInitialMount.current

  // Update previous filters reference
  useEffect(() => {
    previousFiltersRef.current = currentFilterKey
    isInitialMount.current = false
  }, [currentFilterKey])

  // Reset to page 1 when filters change (but not on initial mount)
  useEffect(() => {
    if (filtersHaveChanged && currentPage !== INITIAL_PAGE) {
      setCurrentPageState(INITIAL_PAGE)
      storage.set(CASH_OPERATION_STORAGE_KEYS.CURRENT_PAGE, INITIAL_PAGE)
    }
  }, [filtersHaveChanged, currentPage])

  // Query params
  const queryParams: CashOperationQueryParams = {
    page: currentPage,
    per_page: pageSize,

    ...(filters.operation === 'all'
      ? {}
      : {
          operation: filters.operation,
        }),
    ...(filters.category.includes('all')
      ? {}
      : {
          category: filters.category.join(','),
        }),
    // Note: 'type' filter removed as it's not defined in CashOperationFilterState
    ...(filters.codeCurrency !== 'CUP'
      ? { codeCurrency: filters.codeCurrency }
      : {}),
    ...(filters.dateFrom || filters.dateTo
      ? {
          dateFrom: filters.dateFrom || null,
          dateTo: filters.dateTo || null,
        }
      : {}),
    ...(filters.amountFrom || filters.amountTo
      ? {
          amountFrom: filters.amountFrom || null,
          amountTo: filters.amountTo || null,
        }
      : {}),
    ...(filters.areaIds.length > 0
      ? { areaIds: filters.areaIds.join(',') }
      : {}),
    ...(filters.madeById ? { madeById: parseInt(filters.madeById) } : {}),
    ...(filters.economicCycleIds
      ? { economicCycleIds: filters.economicCycleIds }
      : {}),
  }

  // Debug: Log current filters and query params

  // Query
  const {
    data: cashOperationData,
    isLoading,
    isFetching,
    isError,
    refetch,
    pagination,
  } = useCashOperationQuery({
    params: queryParams,
    options: {
      staleTime: 5 * 60 * 1000,
      retry: 2,
      refetchOnWindowFocus: true,
    },
    businessId: businessData?.id ?? 0,
  })

  // Pagination handlers
  const setCurrentPage = useCallback((page: number) => {
    setCurrentPageState(page)
    storage.set(CASH_OPERATION_STORAGE_KEYS.CURRENT_PAGE, page)
  }, [])

  const setPageSize = useCallback(
    (size: number) => {
      setPageSizeState(size)
      storage.set(CASH_OPERATION_STORAGE_KEYS.PAGE_SIZE, size)
      if (currentPage !== INITIAL_PAGE) setCurrentPage(INITIAL_PAGE)
    },
    [currentPage, setCurrentPage]
  )

  const goToNextPage = useCallback(() => {
    if (pagination.hasNextPage) setCurrentPage(currentPage + 1)
  }, [currentPage, setCurrentPage, pagination.hasNextPage])

  const goToPreviousPage = useCallback(() => {
    if (pagination.hasPreviousPage) setCurrentPage(currentPage - 1)
  }, [currentPage, setCurrentPage, pagination.hasPreviousPage])

  const resetToFirstPage = useCallback(() => {
    if (currentPage !== INITIAL_PAGE) setCurrentPage(INITIAL_PAGE)
  }, [currentPage, setCurrentPage])

  // Sync with API response if there's a mismatch
  useEffect(() => {
    if (
      cashOperationData?.currentPage &&
      cashOperationData.currentPage !== currentPage
    ) {
      setCurrentPageState(cashOperationData.currentPage)
      storage.set(
        CASH_OPERATION_STORAGE_KEYS.CURRENT_PAGE,
        cashOperationData.currentPage
      )
    }
  }, [cashOperationData?.currentPage, currentPage])

  const contextValue: CashOperationTableContextType = {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    cashOperationData: cashOperationData || null,
    isLoading,
    isFetching,
    isError,
    refetch,
    goToNextPage,
    goToPreviousPage,
    skeletonRowCount: pageSize,
    hasNextPage: pagination.hasNextPage,
    hasPreviousPage: pagination.hasPreviousPage,
    resetToFirstPage,
  }

  return (
    <TableContext.Provider value={contextValue}>
      {children}
    </TableContext.Provider>
  )
}

export const useTableContext = () => {
  const context = useContext(TableContext)
  if (context === undefined) {
    throw new Error(
      'useTableContext must be used within a TableContextProvider'
    )
  }
  return context
}
